import React, { useState, useEffect } from 'react';
import styles from './KeyCompetitorsV2.module.scss';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import { getFaviconUrl, getCompanyLogo } from '../../../../../../utils/urlMetadata';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox';
import clsx from 'clsx';

interface AdditionalDetails {
    [key: string]: any;
}

interface Competitor {
    companyName: string;
    description: string;
    website: string;
    location: string;
    revenue: string;
    employees: string;
    foundingYear: string;
    totalFunding: string;
    fundingStage: string;
    topInvestors: string[];
    keyAchievements: string;
    additionalDetails?: AdditionalDetails;
}

interface KeyCompetitorsV2Props {
    competitors: Competitor[];
    isPDFReport?: boolean;
}

interface CompanyMetadata {
    image?: string;
    favicon?: string;
}

interface CompanyMetadataMap {
    [url: string]: CompanyMetadata;
}

const KeyCompetitorsV2: React.FC<KeyCompetitorsV2Props> = ({ competitors, isPDFReport = false }) => {
    const [websiteMetadata, setWebsiteMetadata] = useState<CompanyMetadataMap>({});
    const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        const metadataMap: CompanyMetadataMap = {};

        competitors.forEach((competitor) => {
            if (competitor.website) {
                metadataMap[competitor.website] = {
                    image: getCompanyLogo(competitor.website),
                    favicon: getFaviconUrl(competitor.website),
                };
            }
        });

        setWebsiteMetadata(metadataMap);
        setImageErrors({});
    }, [competitors]);

    if (!competitors || competitors.length === 0) {
        return null;
    }

    // Function to get custom JSX/CSS for specific columns
    const getRowText = (rowKey: string, value: any, competitor: Competitor): React.ReactNode => {
        switch (rowKey) {
            case 'companyName':
                const metadata = websiteMetadata[competitor.website] || {};
                const hasImageError = imageErrors[competitor.website];

                if (isPDFReport) {
                    return (
                        <div className={styles.companyNameCell}>
                            <div className={styles.companyImage}>
                                {metadata?.image && !hasImageError ? (
                                    <img
                                        src={metadata.image}
                                        alt={value}
                                        onError={() => {
                                            setImageErrors((prev) => ({
                                                ...prev,
                                                [competitor.website]: true,
                                            }));
                                        }}
                                    />
                                ) : (
                                    <div className={styles.placeholderImg}>{value?.charAt(0) || ''}</div>
                                )}
                            </div>
                            <div className={styles.companyLink} title={value} style={{ marginTop: '8px', textAlign: 'center' }}>
                                {value}
                            </div>
                        </div>
                    );
                }

                return (
                    <div className={styles.companyNameCell}>
                        <div className={styles.companyImage}>
                            {metadata.image && !hasImageError ? (
                                <img
                                    src={metadata.image}
                                    alt={value}
                                    onError={() => {
                                        setImageErrors((prev) => ({
                                            ...prev,
                                            [competitor.website]: true,
                                        }));
                                    }}
                                />
                            ) : (
                                <div className={styles.placeholderImg}>{value?.charAt(0) || ''}</div>
                            )}
                        </div>
                        {competitor.website ? (
                            <a
                                href={competitor.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${styles.companyLink} ${styles.hasWebsite}`}
                                title={value}
                            >
                                {value}
                                <span className={styles.arrowIcon}>
                                    <TopRightArrowIcon width={14} height={14} style={{ display: 'block' }} />
                                </span>
                            </a>
                        ) : (
                            <div className={styles.companyLink} title={value}>
                                {value}
                            </div>
                        )}
                    </div>
                );
            case 'revenue':
                return <span style={{ fontWeight: '600', color: '#059669' }}>{value}</span>;
            case 'topInvestors':
                if (isPDFReport) {
                    return (
                        <div className={styles.topInvestors}>
                            {Array.isArray(value) && value.length > 0 ? (
                                <div style={{ color: '#000000' }}>{value.join(', ')}</div>
                            ) : !!value?.[0] ? (
                                <div style={{ color: '#000000' }}>{value[0]}</div>
                            ) : (
                                <span>-</span>
                            )}
                        </div>
                    );
                }

                return (
                    <div className={styles.topInvestors}>
                        {Array.isArray(value) && value.length > 0 ? (
                            <>
                                <span className={styles.firstInvestor} title={value[0]}>
                                    {value[0]}
                                </span>
                                {value.length > 1 && (
                                    <Tooltip
                                        direction="top"
                                        style={{ top: '-3.5rem', left: '1.2rem' }}
                                        fitContent={true}
                                        content={
                                            <div>
                                                {value.slice(1).map((investor, index) => (
                                                    <div key={index}>{investor}</div>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <span className={styles.investor}>{` + ${value.length - 1} more`}</span>
                                    </Tooltip>
                                )}
                            </>
                        ) : !!value?.[0] ? (
                            <span className={styles.firstInvestor} title={value[0]}>
                                {value[0]}
                            </span>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );
            default:
                return value;
        }
    };

    // Get all unique additional detail keys from all competitors
    const additionalKeys = new Set<string>();
    competitors.forEach((competitor) => {
        if (competitor.additionalDetails) {
            Object.keys(competitor.additionalDetails).forEach((key) => additionalKeys.add(key));
        }
    });

    // Convert to array and sort for consistent ordering
    const sortedAdditionalKeys = Array.from(additionalKeys).sort();

    // Helper function to format field names for display
    const formatFieldName = (key: string): string => {
        return key
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
    };

    const renderCompetitorsTable = () => (
        <div className={styles.competitorsContent}>
            <div className={styles.tableContainer}>
                <table className={styles.competitorsTable}>
                    <tbody>
                        {/* Standard competitor fields */}
                        {[
                            { key: 'companyName', label: 'Company Name' },
                            { key: 'description', label: 'About' },
                            { key: 'location', label: 'Location' },
                            { key: 'revenue', label: 'Revenue' },
                            { key: 'employees', label: 'Employees' },
                            { key: 'foundingYear', label: 'Founding Year' },
                            { key: 'totalFunding', label: 'Total Funding' },
                            { key: 'fundingStage', label: 'Funding Stage' },
                            { key: 'topInvestors', label: 'Top Investors' },
                            { key: 'keyAchievements', label: 'Key Achievements' },
                        ].map((field) => (
                            <tr key={field.key} className={styles.tableRow}>
                                <td className={styles.rowHeader}>{field.label}</td>
                                {competitors.map((competitor, index) => (
                                    <td key={index} className={`${styles.tableCell} ${index === 0 ? styles.firstCompetitorCell : ''}`}>
                                        {getRowText(field.key, competitor[field.key as keyof Competitor], competitor)}
                                    </td>
                                ))}
                            </tr>
                        ))}

                        {/* Dynamic additional details rows */}
                        {sortedAdditionalKeys.map((additionalKey) => (
                            <tr key={`additional-${additionalKey}`} className={styles.tableRow}>
                                <td className={styles.rowHeader}>{formatFieldName(additionalKey)}</td>
                                {competitors.map((competitor, index) => (
                                    <td key={index} className={`${styles.tableCell} ${index === 0 ? styles.firstCompetitorCell : ''}`}>
                                        {getRowText(additionalKey, competitor.additionalDetails?.[additionalKey] || 'N/A', competitor)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const render = (
        <HeaderBodyBox
            title="Key Competitors"
            headerRight={<AIGeneratedBadge />}
            isPDFReport={isPDFReport}
            className={clsx(styles.competitorsContainer, { [styles.PDFReport]: isPDFReport })}
            bodyClassName={styles.competitorsBody}
        >
            {renderCompetitorsTable()}
        </HeaderBodyBox>
    );

    if (isPDFReport) return competitors.length && reportSectionsToShow.includes(REPORT_SECTION_NAMES.KEY_COMPETITORS) ? render : null;
    else return render;
};

export default KeyCompetitorsV2;
